import EventEmitter from 'owa-event-emitter';
import { orchestrator, ActionCreator, ActionMessage, OrchestratorFunction } from 'satcheljs';
import { useEffect } from 'react';
import { assertNever } from 'owa-assert';

const emitter = new EventEmitter(); // the emitter which keeps track of registered event handlers
const orchestrators: Map<string, OrchestratorFunction<ActionMessage>> = new Map(); // a set of static orchestrators, created on demand, for the given actions

export function useOrchestrator<T extends ActionMessage>(
    action: ActionCreator<T>,
    handler: OrchestratorFunction<T> | undefined,
    dependencies
) {
    const actionId = getActionId(action);

    // the first time we register a non-static event handler for the static action we need
    // to create a static orchestrator for it and keep it on the list; it never goes away since
    // the orchestrator is statically binding itself to the action inside satchel, so we keep
    // it forever here too.
    if (!orchestrators.get(actionId)) {
        const o = orchestrator(action, (actionMessage: T) => {
            // handle the static action by emitting an event, which is forwarded to the non-static handlers
            emitter.emit(actionId, actionMessage);
        }) as OrchestratorFunction<ActionMessage>;

        orchestrators.set(actionId, o);
    }

    // now that the orchestrator is registered, add and remove the event handler
    // when the component mounts & unmounts. take all dependencies into consideration.
    useEffect(() => {
        handler && emitter.on(actionId, handler);
        return () => {
            handler && emitter.off(actionId, handler);
        };
    }, [action, handler, ...dependencies]);
}

function getActionId(action: ActionCreator<ActionMessage>) {
    // __SATCHELJS_ACTION_ID is the stable ID for a Satchel Action.
    const actionId = (action as any).__SATCHELJS_ACTION_ID;
    if (!actionId) {
        assertNever('Binding orchestrator to undefined actionId' as never);
    }
    return actionId;
}
